import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Fab } from '@components/Fab.tsx'
import { Header } from '@components/Header.tsx'
import { SearchInput } from '@components/SearchInput.tsx'
import { useFavicon } from '@hooks/useFavicon.ts'
import {
	useGeolocation,
	useGeolocationPermission,
} from '@hooks/useGeolocation.ts'
import { useLocalStorage } from '@hooks/useLocalStorage.ts'
import { useResource } from '@hooks/useResource.ts'
import { bestScore, MATCH_THRESHOLD } from '@utils/search.ts'
import { api, ApiError } from '@api'
import { useRouter } from '@router'
import type { Activity, ActivityId } from '@hewey/shared'
import { ActivityCard } from './ActivityCard.tsx'
import { ActivityDetail } from './ActivityDetail.tsx'
import { ActivityFilterBar } from './ActivityFilterBar.tsx'
import { ActivityMapPage } from './ActivityMapPage.tsx'
import { ActivityViewToggle } from './ActivityViewToggle.tsx'
import { AddActivityDialog } from './AddActivityDialog.tsx'
import { AllActivitiesPage } from './AllActivitiesPage.tsx'
import { ExploreCollections } from './ExploreCollections.tsx'
import { LocateButton, LocationStatus } from './LocationStatus.tsx'
import { UntaggedActivitiesPage } from './UntaggedActivitiesPage.tsx'
import { UntaggedCallout } from './UntaggedCallout.tsx'
import {
	type ActivityFilter,
	DEFAULT_FILTER,
	isDefaultFilter,
	matchesFilter,
	sortByStale,
	useActivityFilter,
} from './filter.ts'
import { useShortPlaceName } from './placeName.ts'
import { useTodayWeather } from './weather.ts'
import styles from './Activities.module.css'

type View =
	| { kind: 'list' }
	| { kind: 'map' }
	| { kind: 'all' }
	| { kind: 'untagged' }
	| { kind: 'detail'; id: ActivityId }

export const ActivitiesApp = () => {
	useFavicon('activities')
	const { path, navigate } = useRouter()
	// Poll so changes made on another family member's phone show up here.
	const activities = useResource(['activities'], () => api.activity.list(), {
		pollIntervalMs: 10_000,
	})

	const [addOpen, setAddOpen] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [query, setQuery] = useState('')

	const [filter, setFilter] = useActivityFilter()
	const geoPerm = useGeolocationPermission()
	const [locateOptIn, setLocateOptIn] = useLocalStorage<boolean>(
		'hewey.activities.locate',
	)
	// Look up the location on arrival only when that can't show a prompt: the
	// permission is granted, or it can't be read and the user opted in before.
	const autoLocate =
		geoPerm === 'granted' || (geoPerm === 'unknown' && locateOptIn === true)
	const { geo, retry } = useGeolocation(
		filter.maxDistanceMiles !== null || autoLocate,
	)
	const retryLocation = () => {
		setLocateOptIn(true)
		retry()
	}
	const origin = useMemo(
		() =>
			geo.status === 'granted' ? { lat: geo.lat, lng: geo.lng } : null,
		[geo],
	)
	const weather = useTodayWeather(origin)
	const placeName = useShortPlaceName(origin)

	const list = activities.data?.activities ?? []
	const pending = list.filter((a) => a.taggingStatus === 'pending')

	// Explore shows its suggested collections until the user narrows — a search
	// query or any active filter flips it into the results grid.
	const trimmedQuery = query.trim()
	const narrowing = trimmedQuery !== '' || !isDefaultFilter(filter)

	const results = useMemo(() => {
		const matched = list.filter((a) => matchesFilter(a, filter, origin))
		if (trimmedQuery === '') return matched.sort(sortByStale)
		// With a query, rank by forgiving name/place match and drop the rest.
		return matched
			.map((a) => ({
				a,
				score: bestScore(trimmedQuery, [
					a.name,
					a.location.kind === 'place' ? a.location.friendlyName : '',
					a.location.kind === 'place' ? (a.location.town ?? '') : '',
				]),
			}))
			.filter((r) => r.score >= MATCH_THRESHOLD)
			.sort((x, y) => y.score - x.score)
			.map((r) => r.a)
	}, [list, filter, origin, trimmedQuery])

	const clearNarrowing = () => {
		setFilter(DEFAULT_FILTER)
		setQuery('')
	}

	// "See all" on a collection row: replace the filter with the row's patch and
	// clear any query, dropping the user into the matching results grid.
	const onSeeAll = (patch: Partial<ActivityFilter>) => {
		setFilter({ ...DEFAULT_FILTER, ...patch })
		setQuery('')
	}

	const onPatched = (updated: Activity) => {
		activities.set((prev) =>
			prev
				? {
						activities: prev.activities.map((a) =>
							a.id === updated.id ? updated : a,
						),
					}
				: prev,
		)
	}

	// Route within /activities — `/activities/map` is the multi-pin
	// view; `/activities/<id>` is detail; anything else (including a
	// trailing slash) is the list.
	const view = useMemo<View>(() => {
		if (path === '/activities/map') return { kind: 'map' }
		if (path === '/activities/all') return { kind: 'all' }
		if (path === '/activities/untagged') return { kind: 'untagged' }
		const m = /^\/activities\/([^/]+)$/.exec(path)
		if (m) return { kind: 'detail', id: m[1] as ActivityId }
		return { kind: 'list' }
	}, [path])

	if (view.kind === 'map') {
		return (
			<ActivityMapPage activities={list} loading={activities.loading} />
		)
	}

	if (view.kind === 'all') {
		return (
			<AllActivitiesPage activities={list} loading={activities.loading} />
		)
	}

	if (view.kind === 'untagged') {
		return (
			<UntaggedActivitiesPage
				activities={pending}
				loading={activities.loading}
				onPatched={onPatched}
			/>
		)
	}

	if (view.kind === 'detail') {
		const detailId = view.id
		const activity = list.find((a) => a.id === detailId) ?? null
		return (
			<ActivityDetail
				activityId={detailId}
				activity={activity}
				loading={activities.loading}
				onPatched={onPatched}
				onDeleted={(id) => {
					activities.set((prev) =>
						prev
							? {
									activities: prev.activities.filter(
										(a) => a.id !== id,
									),
								}
							: prev,
					)
					navigate('/activities')
				}}
			/>
		)
	}

	const onCreate = async (
		input: Parameters<typeof api.activity.create>[0],
	) => {
		setError(null)
		try {
			const res = await api.activity.create(input)
			activities.set((prev) => ({
				activities: [res.activity, ...(prev?.activities ?? [])],
			}))
			return res.activity
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.message
					: 'Something went wrong adding that activity.',
			)
			throw e
		}
	}

	// Until a lookup runs, the one-tap prompt shows. It waits for the permission
	// read so a granted location doesn't flash it.
	const showLocateButton =
		geo.status === 'idle' &&
		!autoLocate &&
		geoPerm !== null &&
		geoPerm !== 'denied'
	const locationSlot =
		origin !== null ? null : showLocateButton ? (
			<LocateButton onClick={retryLocation} />
		) : (
			<LocationStatus
				geo={geo}
				permission={geoPerm}
				onRetry={retryLocation}
			/>
		)

	return (
		<main className={styles.screen}>
			<Header right={<ActivityViewToggle />} />

			<SearchInput
				value={query}
				onChange={setQuery}
				placeholder="Search activities"
			/>

			<ActivityFilterBar
				filter={filter}
				setFilter={setFilter}
				geo={geo}
				onRetryLocation={retryLocation}
			/>

			<UntaggedCallout count={pending.length} />

			{error ? <p className={styles.error}>{error}</p> : null}

			{activities.error ? (
				<p className={styles.empty}>
					Couldn't load activities.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={() => void activities.refetch()}
					>
						Retry
					</button>
				</p>
			) : activities.loading && list.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : list.length === 0 ? (
				<p className={styles.empty}>
					Nothing yet. Tap + Add to capture an activity.
				</p>
			) : !narrowing ? (
				<ExploreCollections
					activities={list}
					origin={origin}
					weather={weather}
					placeName={placeName}
					locationSlot={locationSlot}
					onSeeAll={onSeeAll}
				/>
			) : results.length === 0 ? (
				<p className={styles.empty}>
					No activities match.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={clearNarrowing}
					>
						Clear search and filters
					</button>
				</p>
			) : (
				<ul className={styles.grid}>
					{results.map((a) => (
						<li key={a.id}>
							<ActivityCard activity={a} />
						</li>
					))}
				</ul>
			)}

			<Fab icon={Plus} label="Add" onClick={() => setAddOpen(true)} />

			<AddActivityDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onCreate={onCreate}
				onPatched={onPatched}
			/>
		</main>
	)
}
